// src/pages/AdminUsers.tsx

import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../utils/apiBase';
import { useAuth } from '../contexts/AuthContext';

interface AdminUserRecord {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'employee' | 'premium' | 'beta' | 'paid' | 'free';
  is_admin: boolean;
  game_credits_total: number;
  created_at: string;
}

const AdminUsers: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadUsers = async () => {
    if (!token) {
      setError('Missing authentication token.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error(err);
      setError('Unable to load users.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [token]);

  const handleUpdate = async (user: AdminUserRecord) => {
    if (!token) {
      return;
    }

    setUpdatingId(user.id);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          role: user.role
        })
      });

      if (!response.ok) {
        throw new Error('Update failed');
      }

      const data = await response.json();
      setUsers((prev) =>
        prev.map((record) => (record.id === user.id ? { ...record, ...data.user } : record))
      );
    } catch (err) {
      console.error(err);
      setError('Failed to update user.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReset = async (userId: number) => {
    if (!token) {
      return;
    }

    setUpdatingId(userId);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Reset failed');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to send reset email.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="section container">
      <h1>User Management</h1>
      <p className="text-secondary">
        Manage user roles, update account info, and send password resets.
      </p>

      {error && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem', backgroundColor: '#fef2f2' }}>
          <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-secondary">Loading users...</p>
      ) : (
        <div className="card" style={{ padding: '1.5rem', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem 0.5rem' }}>Name</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Email</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Role</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Credits</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <input
                      type="text"
                      value={user.name}
                      onChange={(event) =>
                        setUsers((prev) =>
                          prev.map((record) =>
                            record.id === user.id ? { ...record, name: event.target.value } : record
                          )
                        )
                      }
                      style={{ width: '180px', padding: '0.4rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <input
                      type="email"
                      value={user.email}
                      onChange={(event) =>
                        setUsers((prev) =>
                          prev.map((record) =>
                            record.id === user.id ? { ...record, email: event.target.value } : record
                          )
                        )
                      }
                      style={{ width: '220px', padding: '0.4rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <select
                      value={user.role || 'free'}
                      onChange={(event) =>
                        setUsers((prev) =>
                          prev.map((record) =>
                            record.id === user.id ? { ...record, role: event.target.value as AdminUserRecord['role'] } : record
                          )
                        )
                      }
                      style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                      <option value="admin">Admin</option>
                      <option value="employee">Employee</option>
                      <option value="premium">Premium</option>
                      <option value="beta">Beta</option>
                      <option value="paid">Paid</option>
                      <option value="free">Free</option>
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    {user.game_credits_total < 0 ? 'Unlimited' : user.game_credits_total}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleUpdate(user)}
                      disabled={updatingId === user.id}
                      style={{ padding: '0.4rem 0.75rem' }}
                    >
                      {updatingId === user.id ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleReset(user.id)}
                      disabled={updatingId === user.id}
                      style={{ padding: '0.4rem 0.75rem' }}
                    >
                      Send Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
